import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Modal from '../../../components/Utils/ModalShow'; // added
import TableList from '../../../components/Tools/TableList';
import { FaFileAlt, FaCalendarAlt, FaTimesCircle, FaEdit, FaHistory, FaPlus, FaChevronDown, FaChevronUp, FaExclamationTriangle, FaUndo } from 'react-icons/fa';
import RecapCard from "../../../components/Tools/RecapCard";
import { apiService } from '../../../components/Config/api.service';         // added
import { notify } from '../../../components/Utils/Notifications';           // added
import { useCountryConfig } from '../../../components/Config/useCountryConfig';
import { formatMoney, formatPhone } from '../../../components/Utils/FormatUtils';
import { canDeleteSettlement, canEditHrData, canManageContractExpiration } from '../../../components/Auth/permissions';
import LeaveCalculationDetail from './LeaveCalculationDetail';

const ActionCard = ({ title, description, buttonText, icon, onClick, buttonColor = "blue" }) => {
  const colorClasses = {
    blue: "bg-blue-50 text-[--color-primary] hover:bg-blue-100",
    red: "bg-red-50 text-red-600 hover:bg-red-100",
    green: "bg-green-50 text-green-600 hover:bg-green-100"
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 h-full flex flex-col justify-between">
      <div>
        <h3 className="text-lg font-bold mb-2 flex items-center gap-2">{icon}{title}</h3>
        <p className="text-sm text-gray-500 mb-4">{description}</p>
      </div>
      <button
        onClick={onClick}
        className={`px-4 py-2 rounded-lg ${colorClasses[buttonColor]} font-medium transition-all flex items-center justify-center gap-2`}
      >
        {buttonText}
      </button>
    </div>
  );
};

const formatCFA = (amount, locale = 'fr-FR') => {
  if (!amount && amount !== 0) return "______";
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'XOF',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

const InfoSection = ({ title, fields, onUpdate, editLabel, dateLocale }) => (
  <div className="bg-white rounded-xl shadow-sm p-6 h-full">
    <div className="flex justify-between items-center mb-4">
      <h3 className="font-bold flex items-center">
        {title}
      </h3>
      {onUpdate && (
        <button
          onClick={onUpdate}
          className="px-3 py-1.5 bg-blue-50 text-[--color-primary] rounded-lg hover:bg-blue-100 transition-all text-sm font-medium flex items-center gap-1"
        >
          <FaEdit className="text-xs" /> {editLabel}
        </button>
      )}
    </div>
    <div className="grid grid-cols-1 gap-3">
    {fields.map((field, index) => (
      <div key={index} className="flex justify-between py-2 border-b border-gray-100">
        <span className="text-gray-500">{field.label}:</span>
        {field.isLink ? (
          <button
            onClick={field.onClick}
            className="text-[--color-primary] font-medium hover:underline flex items-center gap-1"
          >
            {field.value} <FaChevronDown className="text-xs" />
          </button>
        ) : (
          <span className="font-medium">
            {field.isCurrency ? formatCFA(field.value, dateLocale) : (field.value || "______")}
          </span>
        )}
  </div>
))}
    </div>
  </div>
);

const Contrats = ({ handleDeleteFinalSettlement, finalSettlementRecap, handleDeleteLeaveDeparture, leaveDepartureRecap ,contracts = [], setActiveTab, currentContract, employee, externalContract = null, primes = [], setIsDrawerOpenContracts, setIsDrawerOpenExternalContracts, setIsDrawerOpenInformations, refreshContractInfo }) => { // refreshContractInfo prop added
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.resolvedLanguage?.startsWith('en') ? 'en-US' : 'fr-FR';
  const countryConfig = useCountryConfig();
  const labels = countryConfig.labels;
  const columns = [
    { key: 'Ref', label: t('contractsList.reference') },
    { key: 'Employee', label: t('contractsList.employee') },
    { key: 'DateStart', label: t('contractsList.startDate') },
    { key: 'DateEnd', label: t('contractsList.endDate') },
    {
      key: 'Statut',
      label: t('contractsList.status'),
      render: (row) => {
          let badgeClass = '';
          let statusText = '';

          if (row.expiration_pending) {
              badgeClass = 'badge-warning';
              statusText = t('contractsList.statusExpiringSoon');
          } else {
              switch(row.Statut?.toLowerCase()) {
                  case 'open':
                      badgeClass = 'badge-success';
                      statusText = t('contractsList.statusRunning');
                      break;
                  case 'close':
                      badgeClass = 'badge-error';
                      statusText = t('contractsList.statusExpired');
                      break;
                  case 'draft':
                      badgeClass = 'badge-info';
                      statusText = t('contractsList.statusNew');
                      break;
                  default:
                      badgeClass = 'badge-neutral';
                      statusText = row.Statut || 'N/A';
              }
          }

          return <span className={`badge ${badgeClass}`}>{statusText}</span>;
      }
    },
    {
      key: 'actions',
      label: t('absenceType.actions'),
      render: (row) => {
        if (row.Statut !== 'close' || !canManageContractExpiration()) return null;
        return (
          <button
            onClick={() => openReactivateModal(row)}
            className="px-3 py-1.5 bg-blue-50 text-[--color-primary] rounded-lg hover:bg-blue-100 transition-all text-sm font-medium flex items-center gap-1"
          >
            <FaUndo className="text-xs" /> {t('contractExpiration.reactivate')}
          </button>
        );
      }
    },
  ];
  const [showAllPrimes, setShowAllPrimes] = useState(false);
  const [showHistory, setShowHistory] = useState(true);
  const [showLeaveHistory, setShowLeaveHistory] = useState(false);
  const [leaveHistory, setLeaveHistory] = useState([]);
  const [leaveHistoryError, setLeaveHistoryError] = useState(false);
  const [leaveDetailRow, setLeaveDetailRow] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false); // added
  const [isEndModalOpen, setIsEndModalOpen] = useState(false); // confirmation modal state

  // #276 : contrat arrivé à expiration — prolonger / mettre à expirer / réactiver
  const canManageExpiration = canManageContractExpiration();
  const canEdit = canEditHrData();

  // Départs en congé validés du salarié (rechargés quand le récapitulatif change)
  useEffect(() => {
    if (!employee?.id) return;
    apiService.getElement(`/leave_departure/history/${employee.id}`)
      .then(res => {
        setLeaveHistory(res.data.history || []);
        setLeaveHistoryError(false);
      })
      // Une panne ou un refus n'est pas « aucun congé » : le bloc le dit
      .catch(() => {
        setLeaveHistory([]);
        setLeaveHistoryError(true);
      });
  }, [employee?.id, leaveDepartureRecap]);

  const leaveHistoryColumns = [
    { key: 'date_depart', label: t('employeeProfile.leaveDeparture.departureDate') },
    { key: 'date_retour', label: t('employeeProfile.leaveDeparture.returnDate') },
    { key: 'days', label: t('employeeContracts.leaveHistoryDays'), render: (row) => Number(row.days || 0).toLocaleString(dateLocale, { maximumFractionDigits: 1 }) },
    { key: 'amount', label: t('employeeContracts.leaveHistoryAmount'), render: (row) => formatMoney(row.amount || 0) },
    { key: 'state', label: t('contractsList.status'), render: () => t('employeeProfile.leaveDeparture.validatedBadge') },
    {
      key: 'detail',
      label: t('employeeContracts.leaveHistoryDetail'),
      // Le détail reste consultable une fois le congé terminé
      render: (row) => row.detail && (
        <button
          type="button"
          onClick={() => setLeaveDetailRow(row)}
          className="px-3 py-1.5 bg-blue-50 text-[--color-primary] rounded-lg hover:bg-blue-100 transition-all text-sm font-medium"
        >
          {t('employeeContracts.leaveHistoryDetail')}
        </button>
      ),
    },
  ];
  const [renewalTarget, setRenewalTarget] = useState(null); // { mode: 'renew'|'reactivate', id, ref }
  const [isRenewalModalOpen, setIsRenewalModalOpen] = useState(false);
  const [renewalContractType, setRenewalContractType] = useState('CDD');
  const [renewalDateEnd, setRenewalDateEnd] = useState('');
  const [isExpireModalOpen, setIsExpireModalOpen] = useState(false);

  // end external contract action
  const endExternalContract = () => {
    if (!externalContract?.id) return;
    setIsEndModalOpen(true);
  };

  const confirmEndExternalContract = async () => {
    if (!externalContract?.id) return;
    setIsProcessing(true);
    try {
      await apiService.updateElementById('/external_contract', externalContract.id, { state: 'expired' });
      notify(t('employeeContracts.externalContractEnded'), '', 2);
      if (typeof refreshContractInfo === 'function') refreshContractInfo();
    } catch (err) {
      console.error('Erreur lors de la fin du contrat externe', err);
      notify(t('employeeContracts.externalContractEndError'), '', 3);
    } finally {
      setIsProcessing(false);
      setIsEndModalOpen(false);
    }
  };

  const openRenewModal = () => {
    if (!currentContract?.id) return;
    setRenewalTarget({ mode: 'renew', id: currentContract.id, ref: currentContract.Ref });
    setRenewalContractType(currentContract.contractType || 'CDD');
    setRenewalDateEnd(currentContract.DateEnd || '');
    setIsRenewalModalOpen(true);
  };

  const openReactivateModal = (row) => {
    setRenewalTarget({ mode: 'reactivate', id: row.id, ref: row.Ref });
    setRenewalContractType(row.contractType || 'CDD');
    setRenewalDateEnd(row.DateEnd || '');
    setIsRenewalModalOpen(true);
  };

  const confirmRenewal = async () => {
    if (!renewalTarget?.id) return;
    if (renewalContractType === 'CDD' && !renewalDateEnd) {
      notify(t('contractExpiration.newEndDateRequired'), '', 3);
      return;
    }
    setIsProcessing(true);
    const endpoint = renewalTarget.mode === 'reactivate' ? 'reactivate' : 'renew';
    try {
      await apiService.updateElement(`/contract/${renewalTarget.id}/${endpoint}`, {
        contract_type: renewalContractType,
        date_end: renewalContractType === 'CDD' ? renewalDateEnd : null,
      });
      notify(
        renewalTarget.mode === 'reactivate' ? t('contractExpiration.reactivateSuccess') : t('contractExpiration.renewSuccess'),
        '', 2
      );
      if (typeof refreshContractInfo === 'function') refreshContractInfo();
    } catch (err) {
      console.error('Erreur lors de la mise à jour du contrat', err);
      notify(err?.userMessage || t('contractExpiration.updateError'), '', 3);
    } finally {
      setIsProcessing(false);
      setIsRenewalModalOpen(false);
      setRenewalTarget(null);
    }
  };

  const confirmExpireContract = async () => {
    if (!currentContract?.id) return;
    setIsProcessing(true);
    try {
      await apiService.updateElement(`/contract/${currentContract.id}/expire`, {});
      notify(t('contractExpiration.setExpiredSuccess'), '', 2);
      if (typeof refreshContractInfo === 'function') refreshContractInfo();
    } catch (err) {
      console.error('Erreur lors de la mise à expiration du contrat', err);
      notify(err?.userMessage || t('contractExpiration.setExpiredError'), '', 3);
    } finally {
      setIsProcessing(false);
      setIsExpireModalOpen(false);
    }
  };

  const getPrimeFields = () => {
    const baseFields = [];
    
    // Ajouter les deux premières primes si pas déplié
    if (!showAllPrimes) {
      return [
        ...baseFields,
        ...primes.slice(0, 2).map(prime => ({
          label: prime.name || t('employeeContracts.prime'),
          value: prime.employeeAmount || prime.baseAmount,
          isCurrency: true
        })),
        // Bouton "Voir plus" seulement s'il y a plus de 2 primes
        ...(primes.length > 2 ? [{
          label: t('employeeContracts.seeMore'),
          value: t('employeeContracts.otherPrimes', { count: primes.length - 2 }),
          isLink: true,
          onClick: () => setShowAllPrimes(true)
        }] : [])
      ];
    } else {
      // Montrer toutes les primes si déplié
      return [
        ...baseFields,
        ...primes.map(prime => ({
          label: prime.name || t('employeeContracts.prime'),
          value: prime.employeeAmount || prime.baseAmount,
          isCurrency: true
        })),
        // Bouton pour réduire la liste
        {
          label: t('employeeContracts.collapse'),
          value: "",
          isLink: true,
          onClick: () => setShowAllPrimes(false)
        }
      ];
    }
  };
  
  const openContractDrawer = () => { 
    setIsDrawerOpenContracts(true); 
  };

  const openExternalContractDrawer = () => {
    setIsDrawerOpenExternalContracts(true);
  };

  const openEmployeeDrawer = () => {
    setIsDrawerOpenInformations(true);
  }

  return (
    <div className="p-4 bg-gray-50">      
      {/* Employee Contract Banner */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between">
          <div className="mb-4 md:mb-0">
            <h2 className="text-xl font-bold mb-1">
              {currentContract ? t('employeeContracts.contractName', { ref: currentContract.Ref || '' }) : t('employeeContracts.contractManagement')}
            </h2>
            <p className="text-gray-500">
              {currentContract
                ? t('employeeContracts.activeSince', { date: currentContract.DateStart || '-' })
                : (externalContract ? t('employeeContracts.externalActiveSince', { date: externalContract.date_start || '-' }) : t('employeeContracts.noActiveContract'))}
            </p>
          </div>

          {/* Right-side actions: add / show external / end external */}
          <div>
            {!currentContract && (
              externalContract ? (
                <div className="flex flex-col md:flex-row md:items-center gap-3">
                  <div className="px-3 py-2 bg-yellow-50 text-yellow-800 rounded-md text-sm">
                    {t('employeeContracts.externalActiveWarning')}
                  </div>
                  {/* <div className="flex items-center gap-2">
                    <button
                      onClick={openExternalContractDrawer}
                      className="px-4 py-2 bg-[--color-primary] text-white rounded-lg transition-all font-medium flex items-center gap-2 text-sm"
                    >
                      Voir / Modifier
                    </button>
                    <button
                      onClick={endExternalContract}
                      disabled={isProcessing}
                      className={`px-4 py-2 rounded-lg text-white ${isProcessing ? 'bg-gray-300' : 'bg-red-600 hover:bg-red-700'} text-sm`}
                    >
                      {isProcessing ? 'Traitement...' : 'Terminer le contrat externe'}
                    </button>
                  </div> */}
                </div>
              ) : canEdit && (
                <button
                  onClick={() => openContractDrawer()}
                  className="px-4 py-2 bg-[--color-primary] text-white rounded-lg hover:bg-[--color-primary] transition-all font-medium flex items-center gap-2"
                >
                  <FaPlus size={14} /> {t('employeeContracts.addContract')}
                </button>
              )
            )}
          </div>
        </div>

        {currentContract?.expiration_pending && (
          <div className="mt-4 flex flex-col md:flex-row md:items-center justify-between gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <FaExclamationTriangle size={20} className="text-amber-600 flex-shrink-0" />
              <div>
                <p className="font-semibold text-amber-800">{t('contractExpiration.expiredTitle')}</p>
                <p className="text-sm text-amber-700">
                  {t('contractExpiration.expiredDescription', { date: currentContract.DateEnd || '-' })}
                </p>
              </div>
            </div>
            {canManageExpiration && (
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={openRenewModal}
                  disabled={isProcessing}
                  className={`px-4 py-2 rounded-lg text-white text-sm font-medium ${isProcessing ? 'bg-gray-300' : 'bg-green-600 hover:bg-green-700'}`}
                >
                  {t('contractExpiration.extend')}
                </button>
                <button
                  onClick={() => setIsExpireModalOpen(true)}
                  disabled={isProcessing}
                  className={`px-4 py-2 rounded-lg text-white text-sm font-medium ${isProcessing ? 'bg-gray-300' : 'bg-red-600 hover:bg-red-700'}`}
                >
                  {t('contractExpiration.markAsExpired')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modale partagée : prolonger un contrat en cours / réactiver un contrat expiré (#276) */}
      <Modal
        isOpen={isRenewalModalOpen}
        onClose={() => setIsRenewalModalOpen(false)}
        title={renewalTarget?.mode === 'reactivate' ? t('contractExpiration.reactivateModalTitle') : t('contractExpiration.extendModalTitle')}
        subtitle={t('contractExpiration.contractSubtitle', { ref: renewalTarget?.ref || '—' })}
        size={480}
        footer={
          <>
            <button onClick={() => setIsRenewalModalOpen(false)} className="px-4 py-2 rounded bg-gray-100">{t('basePrimeForm.cancel')}</button>
            <button onClick={confirmRenewal} disabled={isProcessing} className="px-4 py-2 rounded bg-green-600 text-white">
              {isProcessing ? t('contractExpiration.processing') : (renewalTarget?.mode === 'reactivate' ? t('contractExpiration.confirmReactivation') : t('contractExpiration.confirmExtension'))}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('contractExpiration.contractType')}</label>
            <select
              value={renewalContractType}
              onChange={(e) => setRenewalContractType(e.target.value)}
              className="select select-bordered w-full"
            >
              <option value="CDD">CDD</option>
              <option value="CDI">CDI</option>
              <option value="JOURNALIER">Journalier</option>
            </select>
          </div>
          {renewalContractType === 'CDD' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('contractExpiration.newEndDate')}</label>
              <input
                type="date"
                value={renewalDateEnd || ''}
                onChange={(e) => setRenewalDateEnd(e.target.value)}
                className="input input-bordered w-full"
                required
              />
            </div>
          )}
          {renewalContractType === 'CDI' && (
            <p className="text-sm text-gray-500">{t('contractExpiration.cdiNoEndDate')}</p>
          )}
          {renewalContractType === 'JOURNALIER' && (
            <p className="text-sm text-gray-500">{t('contractExpiration.journalierNoEndDate')}</p>
          )}
        </div>
      </Modal>

      {/* Modale : mettre fin au contrat arrivé à expiration */}
      <Modal
        isOpen={isExpireModalOpen}
        onClose={() => setIsExpireModalOpen(false)}
        title={t('contractExpiration.setExpiredModalTitle')}
        subtitle={t('contractExpiration.contractSubtitle', { ref: currentContract?.Ref || '—' })}
        size={480}
        footer={
          <>
            <button onClick={() => setIsExpireModalOpen(false)} className="px-4 py-2 rounded bg-gray-100">{t('basePrimeForm.cancel')}</button>
            <button onClick={confirmExpireContract} disabled={isProcessing} className="px-4 py-2 rounded bg-red-600 text-white">
              {isProcessing ? t('contractExpiration.processing') : t('contractExpiration.confirmEndContract')}
            </button>
          </>
        }
      >
        <p>{t('contractExpiration.confirmEndQuestion')}</p>
        <p className="text-sm text-gray-500 mt-3">{t('contractExpiration.reversibleNote')}</p>
      </Modal>

      {currentContract ? (
        <div className="space-y-6">
          {/* Action Cards */}
          {canEdit && (
          <div className={` grid grid-cols-1 gap-3 ${(finalSettlementRecap || leaveDepartureRecap) ? 'mb-6 md:grid-cols-2' : 'md:grid-cols-3'}`}>
            {/*
            <ActionCard 
              title="Générer un document"
              description="Générer un contrat ou autre document lié à cet employé."
              buttonText="Générer un document"
              icon={<FaFileAlt size={24} className="text-[--color-primary]" />}
              onClick={() => {}}
              buttonColor="blue"
            />
            */}

            {!leaveDepartureRecap && (
            <ActionCard
              title={t('employeeContracts.leaveDepartureTitle')}
              description={t('employeeContracts.leaveDepartureDescription')}
              buttonText={t('employeeContracts.configureLeave')}
              icon={<FaCalendarAlt size={24} className="text-green-600" />}
              onClick={() => setActiveTab("LeaveDeparture")}
              buttonColor="green"
            />
            )}

            {!finalSettlementRecap && (
              <ActionCard
                title={t('employeeContracts.endOfContractTitle')}
                description={t('employeeContracts.endOfContractDescription')}
                buttonText={t('employeeContracts.configureEnd')}
                icon={<FaTimesCircle size={24} className="text-red-600" />}
                onClick={() => setActiveTab("FinalSettlement")}
                buttonColor="red"
              />
            )}
          </div>
          )}

          {leaveDepartureRecap && (
            <RecapCard
              icon={<FaCalendarAlt size={28} className="text-[var(--color-primary)]" />}
              title={t('employeeContracts.leaveRecapTitle')}
              state={leaveDepartureRecap.state}
              stateLabel={leaveDepartureRecap.state === "draft" ? t('employeeProfile.leaveDeparture.draftBadge') : t('employeeProfile.leaveDeparture.validatedBadge')}
              fields={[
                { label: t('employeeProfile.leaveDeparture.departureDate') + ' :', value: leaveDepartureRecap.date_depart },
                { label: t('employeeProfile.leaveDeparture.returnDate') + ' :', value: leaveDepartureRecap.date_retour },
              ]}
              info={
                leaveDepartureRecap.state === "draft"
                  ? t('employeeContracts.leaveRecapSaved')
                  : t('employeeContracts.leaveRecapValidated')
              }
              onEdit={canEdit ? () => setActiveTab("LeaveDeparture") : undefined}
              onDelete={handleDeleteLeaveDeparture}
              showDelete={canEdit && leaveDepartureRecap.state === "draft"}
            />
          )}

          {finalSettlementRecap && (
            <RecapCard
              icon={<FaHistory size={28} className="text-[var(--color-primary)]" />}
              title={t('employeeContracts.settlementRecapTitle')}
              state={finalSettlementRecap.state}
              stateLabel={{
                draft: t('employeeInfo.finalSettlement.draftBadge'),
                paid: t('employeeInfo.finalSettlement.paidBadge'),
              }[finalSettlementRecap.state] || t('employeeInfo.finalSettlement.validatedBadge')}
              fields={[
                { label: t('employeeContracts.reason') + ' :', value: finalSettlementRecap.departure_reason_name },
                { label: t('employeeInfo.finalSettlement.exitDate') + ' :', value: finalSettlementRecap.exit_date },
                {
                  label: t('employeeInfo.finalSettlement.balanceDue') + ' :',
                  value: (
                    <span className="font-bold text-xl ml-2 text-[var(--color-primary)]">
                      {formatMoney(Number(finalSettlementRecap.total_amount) || 0)}
                    </span>
                  ),
                },
              ]}
              info={
                finalSettlementRecap.gere_par_rh
                  ? t('employeeContracts.settlementRecapManagedByHr')
                  : finalSettlementRecap.state === "draft"
                    ? t('employeeContracts.settlementRecapSaved')
                    : t('employeeContracts.settlementRecapValidated')
              }
              // Un solde géré par le départ RH se consulte depuis la Paie, sans
              // s'y modifier ni s'y supprimer (Yros/Kavero#8).
              onEdit={canEdit ? () => setActiveTab("FinalSettlement") : undefined}
              editLabel={finalSettlementRecap.gere_par_rh ? t('employeeContracts.settlementRecapView') : undefined}
              onDelete={handleDeleteFinalSettlement}
              showDelete={canEdit && !finalSettlementRecap.gere_par_rh && finalSettlementRecap.state === "draft" && canDeleteSettlement()}
            />
          )}

          {/* Contract Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <InfoSection
              title={t('employeeContracts.personalInfo')}
              editLabel={t('employeeContracts.edit')}
              dateLocale={dateLocale}
              fields={[
                { label: t('employeeContracts.birthDate'), value: employee?.birthday },
                { label: t('employeeContracts.address'), value: employee?.address },
                { label: t('employeeContracts.phoneNumber'), value: formatPhone(employee?.phone) },
                { label: t('employeeContracts.email'), value: employee?.email }
              ]}
              onUpdate={canEdit ? openEmployeeDrawer : undefined}
            />

            <InfoSection
              title={t('employeeContracts.contractSettings')}
              editLabel={t('employeeContracts.edit')}
              dateLocale={dateLocale}
              fields={[
                { label: t('employeeContracts.contractType'), value: currentContract?.contractType},
                { label: labels.part_ir, value: currentContract?.part_ir},
                { label: t('employeeContracts.startDate'), value: currentContract?.DateStart },
                { label: t('employeeContracts.endDate'), value: currentContract?.DateEnd },
              ]}
              onUpdate={canEdit ? openContractDrawer : undefined}
            />

            <InfoSection
              title={t('employeeContracts.salaryInfo')}
              editLabel={t('employeeContracts.edit')}
              dateLocale={dateLocale}
              fields={[
                { label: t('employeeContracts.category'), value: currentContract?.category },
                { label: t('employeeContracts.convention'), value: currentContract?.convention },
                { label: t('employeeContracts.baseSalary'), value: currentContract?.base_salary, isCurrency: true },
                { label: t('employeeContracts.overSalary'), value: currentContract?.sursalaire, isCurrency: true }
              ]}
              onUpdate={canEdit ? openContractDrawer : undefined}
            />

            <InfoSection
              title={t('employeeContracts.primesAndBenefits')}
              editLabel={t('employeeContracts.edit')}
              dateLocale={dateLocale}
              fields={getPrimeFields()}
              onUpdate={canEdit ? () => {
                window.location.href = "/primes";
              } : undefined}
            />
          </div>
        </div>
      ) : externalContract ? (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <div className="max-w-3xl mx-auto flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex items-center gap-4 w-full md:w-2/3">
                <div className="bg-yellow-50 rounded-lg p-3 flex items-center justify-center">
                  <FaFileAlt size={28} className="text-yellow-600" />
                </div>
                <div className="flex-1 text-left">
                  <div className="text-sm text-gray-500">{t('employeeContracts.externalContract')}</div>
                  <div className="font-medium text-lg">{externalContract.name || t('employeeContracts.externalContract')}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm text-gray-500">
                      {externalContract.type ? `${externalContract.type}` : ''}
                    </span>
                    {externalContract.state && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-medium">
                        {externalContract.state}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500 mt-2">
                    {externalContract.date_start || '—'} → {externalContract.date_end || '—'}
                  </div>
                  <div className="mt-2 font-semibold text-[--color-primary]">
                    {externalContract.net_salary ? new Intl.NumberFormat(dateLocale,{ style:'currency', currency:'XOF', maximumFractionDigits:0}).format(externalContract.net_salary) : '—'}
                  </div>
                </div>
              </div>
              <div className="flex-shrink-0 flex flex-col items-end gap-2 w-full md:w-1/3">
                {canEdit && (
                <>
                <div className="text-xs text-gray-500 text-right mb-1">{t('employeeContracts.actions')}</div>
                <div className="flex gap-2">
                  <button
                    onClick={openExternalContractDrawer}
                    className="px-3 py-2 bg-[--color-primary] text-white rounded-lg text-sm"
                  >
                    {t('employeeContracts.edit')}
                  </button>
                  <button
                    onClick={endExternalContract}
                    disabled={isProcessing}
                    className={`px-3 py-2 rounded-lg text-white ${isProcessing ? 'bg-gray-300' : 'bg-red-600 hover:bg-red-700'} text-sm`}
                  >
                    {isProcessing ? t('employeeContracts.processing') : t('employeeContracts.end')}
                  </button>
                </div>
                <div className="text-xs text-gray-400 mt-2">{t('employeeContracts.mustEndExternalFirst')}</div>
                </>
                )}
              </div>
            </div>
            {/* Confirmation modal for ending external contract */}
            <Modal
              isOpen={isEndModalOpen}
              onClose={() => setIsEndModalOpen(false)}
              title={t('employeeContracts.endExternalContractTitle')}
              subtitle={t('employeeContracts.contractName', { ref: externalContract?.name || '—' })}
              size={480}
              footer={
                <>
                  <button onClick={() => setIsEndModalOpen(false)} className="px-4 py-2 rounded bg-gray-100">{t('employeeContracts.cancel')}</button>
                  <button onClick={confirmEndExternalContract} disabled={isProcessing} className="px-4 py-2 rounded bg-red-600 text-white">
                    {isProcessing ? t('employeeContracts.processing') : t('employeeContracts.confirmEnd')}
                  </button>
                </>
              }
            >
              <p>{t('employeeContracts.confirmEndQuestion')}</p>
              <p className="text-sm text-gray-500 mt-3">{t('employeeContracts.confirmEndNote')}</p>
            </Modal>
          </div>
           ) : (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center mb-6">
            <div className="bg-gray-50 h-20 w-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaFileAlt size={32} className="text-gray-400" />
            </div>
            <h3 className="text-xl font-bold mb-2">{t('employeeContracts.noCurrentContract')}</h3>
            <p className="text-gray-500 mb-6 max-w-lg mx-auto">
              {t('employeeContracts.noCurrentContractDetail')}
            </p>
            {canEdit && (
            <div className='flex justify-center gap-4'>
              <button
                onClick={openContractDrawer}
                className="px-5 py-2.5 bg-[--color-primary] text-white rounded-lg transition-all font-medium flex items-center gap-2 mx-auto"
              >
                <FaPlus size={14} /> {t('employeeContracts.addContract')}
              </button>

              <button
                onClick={openExternalContractDrawer}
                className="px-5 py-2.5 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-all font-medium flex items-center gap-2 mx-auto"
              >
                <FaPlus size={14} /> {t('employeeContracts.internOrContractor')}
              </button>
            </div>
            )}
          </div>
        )}

      {/* Leave History */}
      <div className="bg-white rounded-xl shadow-sm p-6 mt-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold flex items-center">
            <span className="w-2 h-6 bg-[var(--color-primary)] rounded-sm mr-2"></span>
            {t('employeeContracts.leaveHistory')}
          </h3>
          <button
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all text-sm font-medium flex items-center gap-2"
            onClick={() => setShowLeaveHistory(!showLeaveHistory)}
          >
            {showLeaveHistory ? (
              <>
                <FaChevronUp size={14} /> {t('employeeContracts.hideHistory')}
              </>
            ) : (
              <>
                <FaChevronDown size={14} /> {t('employeeContracts.showHistory')}
              </>
            )}
          </button>
        </div>

        {showLeaveHistory && (
          leaveHistoryError ? (
            <div role="alert" className="py-8 text-center text-red-600">
              <FaExclamationTriangle size={32} className="mx-auto mb-3" />
              <p>{t('employeeContracts.leaveHistoryError')}</p>
            </div>
          ) : leaveHistory.length > 0 ? (
            <div className="overflow-x-auto">
              <TableList columns={leaveHistoryColumns} data={leaveHistory} searchDisplay={false} />
            </div>
          ) : (
            <div className="py-8 text-center text-gray-500">
              <FaCalendarAlt size={32} className="text-gray-300 mx-auto mb-3" />
              <p>{t('employeeContracts.noLeaveHistory')}</p>
            </div>
          )
        )}
        <Modal
          isOpen={!!leaveDetailRow}
          onClose={() => setLeaveDetailRow(null)}
          title={t('employeeProfile.leaveDeparture.detailTitle')}
          size={640}
        >
          {leaveDetailRow && (
            <LeaveCalculationDetail detail={leaveDetailRow.detail} gestionType={leaveDetailRow.company_gestion_type} />
          )}
        </Modal>
      </div>

      {/* Contract History */}
      <div className="bg-white rounded-xl shadow-sm p-6 mt-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold flex items-center">
            <span className="w-2 h-6 bg-purple-600 rounded-sm mr-2"></span>
            {t('employeeContracts.contractHistory')}
          </h3>
          <button
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all text-sm font-medium flex items-center gap-2"
            onClick={() => setShowHistory(!showHistory)}
          >
            {showHistory ? (
              <>
                <FaChevronUp size={14} /> {t('employeeContracts.hideHistory')}
              </>
            ) : (
              <>
                <FaChevronDown size={14} /> {t('employeeContracts.showHistory')}
              </>
            )}
          </button>
        </div>

        {showHistory && (
          contracts.length > 0 ? (
            <div className="overflow-x-auto">
              <TableList columns={columns} data={contracts} searchDisplay={false} />
            </div>
          ) : (
            <div className="py-8 text-center text-gray-500">
              <FaHistory size={32} className="text-gray-300 mx-auto mb-3" />
              <p>{t('employeeContracts.noContractHistory')}</p>
            </div>
          )
        )}
      </div>
    </div>
  );
};

ActionCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  buttonText: PropTypes.string.isRequired,
  icon: PropTypes.node,
  onClick: PropTypes.func.isRequired,
  buttonColor: PropTypes.oneOf(['blue', 'red', 'green']),
};

InfoSection.propTypes = {
  title: PropTypes.string.isRequired,
  fields: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.any,
    isCurrency: PropTypes.bool,
    isLink: PropTypes.bool,
    onClick: PropTypes.func,
  })).isRequired,
  onUpdate: PropTypes.func,
  editLabel: PropTypes.string,
  dateLocale: PropTypes.string,
};

Contrats.propTypes = {
  handleDeleteFinalSettlement: PropTypes.func,
  finalSettlementRecap: PropTypes.object,
  handleDeleteLeaveDeparture: PropTypes.func,
  leaveDepartureRecap: PropTypes.object,
  contracts: PropTypes.array,
  setActiveTab: PropTypes.func.isRequired,
  currentContract: PropTypes.object,
  employee: PropTypes.object,
  externalContract: PropTypes.object,
  primes: PropTypes.array,
  setIsDrawerOpenContracts: PropTypes.func.isRequired,
  setIsDrawerOpenExternalContracts: PropTypes.func.isRequired,
  setIsDrawerOpenInformations: PropTypes.func.isRequired,
  refreshContractInfo: PropTypes.func,
};

export default Contrats;
